/**
 * <plus-interactive-card-v2>
 * Reason cards that reveal their details: on desktop the details replace the card's front, on mobile
 * they open below the card like an accordion (both are pure CSS on the `is-open` class).
 * Only one card is open at a time. A click anywhere on a card toggles it (links inside the details
 * still work); the plus button carries aria-expanded for keyboard and screen-reader users.
 * In the theme editor, selecting a card block opens it.
 */
class PlusInteractiveCardV2 extends HTMLElement {
  connectedCallback() {
    this.onClick = (event) => this.handleClick(event);
    this.onBlockSelect = (event) => {
      const card = this.cardFrom(event.target);
      if (card) this.open(card);
    };
    this.onBlockDeselect = (event) => {
      const card = this.cardFrom(event.target);
      if (card) this.setOpen(card, false);
    };

    // The front is only covered on desktop, so re-check open cards when the layout switches.
    this.desktopQuery = window.matchMedia('(min-width: 990px)');
    this.onLayoutChange = () => {
      this.querySelectorAll('[data-card].is-open').forEach((card) => this.setOpen(card, true));
    };

    this.addEventListener('click', this.onClick);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.desktopQuery.addEventListener?.('change', this.onLayoutChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    this.desktopQuery?.removeEventListener?.('change', this.onLayoutChange);
  }

  /** @param {EventTarget | null} target */
  cardFrom(target) {
    if (!(target instanceof Element)) return null;
    const card = target.closest('[data-card]');
    return card && this.contains(card) ? card : null;
  }

  /** @param {MouseEvent} event */
  handleClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const card = this.cardFrom(target);
    if (!card || !target) return;
    if (target.closest('a[href]')) return;
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && card.contains(selection.anchorNode)) return;

    if (card.classList.contains('is-open')) this.setOpen(card, false);
    else this.open(card);
  }

  /** @param {Element} card */
  open(card) {
    this.querySelectorAll('[data-card].is-open').forEach((other) => {
      if (other !== card) this.setOpen(other, false);
    });
    this.setOpen(card, true);
  }

  /**
   * @param {Element} card
   * @param {boolean} open
   */
  setOpen(card, open) {
    card.classList.toggle('is-open', open);
    card.querySelector('[data-toggle]')?.setAttribute('aria-expanded', String(open));
    const details = card.querySelector('[data-details]');
    if (details instanceof HTMLElement) details.inert = !open;
    // On desktop the details cover the front, so hide the front from assistive tech while open.
    const front = card.querySelector('[data-front]');
    if (front instanceof HTMLElement) {
      if (open && this.desktopQuery?.matches) front.setAttribute('aria-hidden', 'true');
      else front.removeAttribute('aria-hidden');
    }
  }
}

if (!customElements.get('plus-interactive-card-v2')) {
  customElements.define('plus-interactive-card-v2', PlusInteractiveCardV2);
}
